<template>
  <div class="min-h-screen bg-blue-50">
    <header class="fixed top-0 left-0 right-0 h-16 bg-white/95 backdrop-blur-md shadow-sm z-[1001] border-b border-blue-100">
      <div class="h-full flex items-center justify-between max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 gap-3 text-left">
        <div class="flex items-center min-w-0 flex-1">
          <button
            @click="handleBack"
            class="flex items-center justify-center h-9 w-9 rounded-full bg-blue-50 hover:bg-blue-100 text-blue-900 hover:text-yellow-500 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-blue-900 focus:ring-offset-2 mr-3 shrink-0"
            aria-label="Back"
          >
            <span class="material-symbols-outlined text-2xl">arrow_back</span>
          </button>
          <div
            v-if="activeConversation && isMobileView"
            class="h-10 w-10 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center shrink-0 overflow-hidden mr-3"
          >
            <img
              v-if="canShowAvatar(activeAvatarKey, activeConversationAvatar)"
              :src="activeConversationAvatar"
              :alt="activeConversationTitle"
              class="h-full w-full object-cover"
              @error="markAvatarFailed(activeAvatarKey)"
            />
            <span v-else-if="activeConversationIsDepartment" class="material-symbols-outlined">account_balance</span>
            <span v-else-if="activeConversationIsGroup" class="material-symbols-outlined">groups</span>
            <span v-else class="font-semibold">{{ initials(activeConversationTitle) }}</span>
          </div>
          <div class="min-w-0 text-left">
            <div
              v-if="activeConversation && isMobileView"
              class="flex items-center gap-1.5 min-w-0"
            >
              <h1 class="font-semibold text-gray-900 truncate">{{ activeConversationTitle }}</h1>
              <img
                v-if="activeConversationIsDepartment"
                :src="verifiedBadge"
                alt="Verified"
                class="h-4 w-4 shrink-0"
              />
            </div>
            <h1 v-else class="font-semibold text-gray-900 truncate">Chats</h1>
            <p class="text-xs text-gray-500 truncate">
              {{ activeConversation && isMobileView ? activeConversationSubtitle : 'Direct, group, and department conversations' }}
            </p>
          </div>
        </div>

        <div class="flex items-center gap-2 shrink-0">
          <button
            v-if="activeConversationSupportsDetails && isMobileView"
            @click="openDetailsPanel"
            class="h-10 w-10 rounded-full bg-blue-50 text-blue-700 hover:bg-blue-100 transition-colors flex items-center justify-center"
            :aria-label="activeConversationIsDepartment ? 'View department details' : 'View group details'"
          >
            <span class="material-symbols-outlined text-xl">info</span>
          </button>
          <button
            v-if="!activeConversation"
            @click="openDepartmentModal"
            class="hidden md:inline-flex items-center gap-2 px-3 py-2 bg-blue-50 text-blue-700 rounded-lg hover:bg-blue-100 transition-colors text-sm font-medium"
          >
            <span class="material-symbols-outlined text-lg">account_balance</span>
            <span class="hidden sm:inline">Departments</span>
          </button>
          <button
            v-if="isAdminUser && !activeConversation"
            @click="openCreateDepartmentModal"
            class="hidden md:inline-flex items-center gap-2 px-3 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors text-sm font-medium"
          >
            <span class="material-symbols-outlined text-lg">verified</span>
            <span class="hidden sm:inline">New Department</span>
          </button>
          <button
            v-if="hasConnections && !activeConversation"
            @click="openGroupModal"
            class="hidden md:inline-flex items-center gap-2 px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium"
          >
            <span class="material-symbols-outlined text-lg">group_add</span>
            <span class="hidden sm:inline">New Group</span>
          </button>
        </div>
      </div>
    </header>

    <main class="pt-16 h-screen max-w-7xl mx-auto md:px-4 lg:px-8">
      <div class="h-full md:py-4">
        <div class="h-full bg-white md:rounded-lg md:shadow overflow-hidden flex">
          <aside
            class="h-full w-full md:w-80 lg:w-96 border-r border-gray-100 flex-col bg-white"
            :class="activeConversation ? 'hidden md:flex' : 'flex'"
          >
            <div class="p-4 border-b border-gray-100 bg-gray-50">
              <div class="flex items-center justify-between gap-3">
                <div class="text-left">
                  <h2 class="font-semibold text-gray-900">Inbox</h2>
                  <p class="text-xs text-gray-500">{{ displayConversations.length }} conversation{{ displayConversations.length === 1 ? '' : 's' }}</p>
                </div>
                <div class="flex items-center gap-2">
                  <button
                    @click="openDepartmentModal"
                    class="h-10 w-10 rounded-full bg-blue-50 text-blue-700 hover:bg-blue-100 transition-colors flex items-center justify-center"
                    aria-label="Message department"
                  >
                    <span class="material-symbols-outlined text-xl">account_balance</span>
                  </button>
                  <button
                    v-if="isAdminUser"
                    @click="openCreateDepartmentModal"
                    class="h-10 w-10 rounded-full bg-emerald-600 text-white hover:bg-emerald-700 transition-colors flex items-center justify-center"
                    aria-label="Create department profile"
                  >
                    <span class="material-symbols-outlined text-xl">verified</span>
                  </button>
                  <button
                    v-if="hasConnections"
                    @click="openGroupModal"
                    class="h-10 w-10 rounded-full bg-blue-600 text-white hover:bg-blue-700 transition-colors flex items-center justify-center"
                    aria-label="Create group"
                  >
                    <span class="material-symbols-outlined text-xl">group_add</span>
                  </button>
                </div>
              </div>
            </div>

            <div class="flex-1 overflow-y-auto">
              <div v-if="chatsLoading" class="flex flex-col items-center justify-center h-40 p-4">
                <div class="w-8 h-8 border-4 border-blue-200 border-t-blue-800 rounded-full animate-spin"></div>
                <p class="mt-2 text-sm text-gray-600">Loading conversations...</p>
              </div>

              <div v-else-if="displayConversations.length === 0" class="flex flex-col items-center justify-center h-52 p-6 text-center">
                <span class="material-symbols-outlined text-4xl text-gray-300">forum</span>
                <p class="mt-2 text-sm font-medium text-gray-700">No conversations yet</p>
                <p class="text-xs text-gray-500 mt-1">Message a department, start a direct chat, or create a group.</p>
              </div>

              <div v-else class="divide-y divide-gray-100">
                <conversation-item
                  v-for="conversation in displayConversations"
                  :key="conversation.id"
                  :conversation="conversation"
                  :current-user-id="userId"
                  :can-delete="canDeleteConversation(conversation)"
                  :is-active="activeConversation?.id === conversation.id"
                  @click="selectConversation(conversation)"
                  @delete="requestDeleteConversation"
                />
              </div>
            </div>
          </aside>

          <section
            class="h-full flex-1 flex-col min-w-0 bg-white"
            :class="activeConversation ? 'flex' : 'hidden md:flex'"
          >
            <div v-if="!activeConversation" class="flex-1 flex flex-col items-center justify-center text-center bg-gray-50 p-8">
              <span class="material-symbols-outlined text-5xl text-gray-300 mb-4">chat</span>
              <h3 class="text-xl font-medium text-gray-700 mb-2">Select a conversation</h3>
              <p class="text-gray-500 max-w-sm">Choose a chat from the inbox or message a verified department profile.</p>
            </div>

            <template v-else>
              <div class="hidden md:flex items-center justify-between gap-3 px-4 py-3 border-b border-gray-100 bg-white">
                <div class="flex items-center min-w-0">
                  <div class="h-10 w-10 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center shrink-0 overflow-hidden">
                    <img
                      v-if="canShowAvatar(activeAvatarKey, activeConversationAvatar)"
                      :src="activeConversationAvatar"
                      :alt="activeConversationTitle"
                      class="h-full w-full object-cover"
                      @error="markAvatarFailed(activeAvatarKey)"
                    />
                    <span v-else-if="activeConversationIsDepartment" class="material-symbols-outlined">account_balance</span>
                    <span v-else-if="activeConversationIsGroup" class="material-symbols-outlined">groups</span>
                    <span v-else class="font-semibold">{{ initials(activeConversationTitle) }}</span>
                  </div>
                  <div class="ml-3 min-w-0 text-left">
                    <div class="flex items-center gap-1.5 min-w-0">
                      <h2 class="font-semibold text-gray-900 truncate">{{ activeConversationTitle }}</h2>
                      <img
                        v-if="activeConversationIsDepartment"
                        :src="verifiedBadge"
                        alt="Verified"
                        class="h-4 w-4 shrink-0"
                      />
                    </div>
                    <p class="text-xs text-gray-500 truncate">{{ activeConversationSubtitle }}</p>
                  </div>
                </div>
                <div class="flex items-center gap-2 shrink-0">
                  <button
                    v-if="activeConversationSupportsDetails"
                    @click="openDetailsPanel"
                    class="h-10 w-10 rounded-full bg-blue-50 text-blue-700 hover:bg-blue-100 transition-colors flex items-center justify-center"
                    :aria-label="activeConversationIsDepartment ? 'View department details' : 'View group details'"
                  >
                    <span class="material-symbols-outlined text-xl">info</span>
                  </button>
                  <button
                    v-if="canDeleteActiveConversation"
                    @click="requestDeleteConversation(activeConversation)"
                    class="h-10 w-10 rounded-full bg-red-50 text-red-600 hover:bg-red-100 transition-colors flex items-center justify-center"
                    aria-label="Delete conversation"
                  >
                    <span class="material-symbols-outlined text-xl">delete</span>
                  </button>
                </div>
              </div>

              <div class="flex-1 min-h-0 bg-gray-50">
                <message-list
                  :messages="messages"
                  :current-user-id="userId"
                  :loading="loadingMessages"
                  :show-sender-names="activeConversationIsGroup || activeConversationIsDepartment"
                  :participants="activeConversation.participantDetails || []"
                  :owned-sender-ids="activeOwnedSenderIds"
                  class="h-full"
                />
              </div>

              <message-input
                @send="sendMessage"
                :disabled="sendingMessage"
                class="border-t border-gray-100"
              />
            </template>
          </section>
        </div>
      </div>
    </main>

    <div
      v-if="showDepartmentModal"
      class="fixed inset-0 z-[1100] bg-black/40 flex items-end sm:items-center justify-center"
      @click.self="closeDepartmentModal"
    >
      <div class="bg-white w-full sm:max-w-lg sm:rounded-lg max-h-[92vh] overflow-hidden flex flex-col text-left">
        <div class="p-4 border-b border-gray-100 flex items-center justify-between gap-3">
          <div>
            <h2 class="text-lg font-semibold text-gray-900">Message Department</h2>
            <p class="text-xs text-gray-500">Choose a verified department profile.</p>
          </div>
          <button
            @click="closeDepartmentModal"
            class="h-9 w-9 rounded-full bg-gray-100 text-gray-600 hover:bg-gray-200 flex items-center justify-center"
            aria-label="Close"
          >
            <span class="material-symbols-outlined text-xl">close</span>
          </button>
        </div>

        <div class="p-4 space-y-4 overflow-y-auto">
          <label class="block">
            <span class="text-sm font-medium text-gray-700">Find Department</span>
            <div class="mt-1 relative">
              <span class="material-symbols-outlined text-gray-400 absolute left-3 top-1/2 -translate-y-1/2">search</span>
              <input
                v-model.trim="departmentSearch"
                type="search"
                class="w-full border border-gray-200 rounded-lg pl-10 pr-4 py-3 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Search departments"
              />
            </div>
          </label>

          <div v-if="loadingDepartments" class="flex items-center justify-center py-8">
            <div class="w-7 h-7 border-4 border-blue-200 border-t-blue-800 rounded-full animate-spin"></div>
          </div>

          <div v-else-if="filteredDepartmentProfiles.length === 0" class="text-center py-8">
            <span class="material-symbols-outlined text-4xl text-gray-300">account_balance</span>
            <p class="text-sm text-gray-500 mt-2">No department profiles found.</p>
          </div>

          <div v-else class="divide-y divide-gray-100 border border-gray-100 rounded-lg overflow-hidden">
            <div
              v-for="department in filteredDepartmentProfiles"
              :key="department.id"
              class="p-3 flex items-center gap-2 hover:bg-blue-50 transition-colors"
            >
              <button
                type="button"
                class="flex-1 min-w-0 flex items-center gap-3 text-left"
                @click="openDepartmentConversation(department)"
              >
                <div class="h-11 w-11 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center shrink-0 overflow-hidden">
                  <img
                    v-if="canShowAvatar(departmentProfileAvatarKey(department.id), department.profilePicture)"
                    :src="department.profilePicture"
                    :alt="department.name"
                    class="h-full w-full object-cover"
                    @error="markAvatarFailed(departmentProfileAvatarKey(department.id))"
                  />
                  <span v-else class="material-symbols-outlined text-2xl">account_balance</span>
                </div>
                <div class="flex-1 min-w-0 text-left">
                  <div class="flex items-center gap-1.5 min-w-0">
                    <p class="font-medium text-gray-900 truncate">{{ department.name }}</p>
                    <img :src="verifiedBadge" alt="Verified" class="h-4 w-4 shrink-0" />
                  </div>
                  <p class="text-xs text-blue-700 truncate">Verified department profile</p>
                </div>
                <span class="material-symbols-outlined text-gray-300 shrink-0">chevron_right</span>
              </button>
              <button
                v-if="isAdminUser"
                type="button"
                class="h-9 w-9 rounded-full text-gray-400 hover:text-blue-700 hover:bg-blue-50 transition-colors flex items-center justify-center shrink-0 relative overflow-hidden"
                aria-label="Upload department profile picture"
                @click.stop
              >
                <input
                  type="file"
                  accept="image/*"
                  class="absolute inset-0 opacity-0 cursor-pointer"
                  :disabled="uploadingDepartmentImage"
                  @change="handleDepartmentImageFile(department, $event)"
                />
                <span class="material-symbols-outlined text-lg">add_photo_alternate</span>
              </button>
              <button
                v-if="isAdminUser && department.profilePicture"
                type="button"
                class="h-9 w-9 rounded-full text-gray-400 hover:text-red-600 hover:bg-red-50 transition-colors flex items-center justify-center shrink-0"
                aria-label="Remove department profile picture"
                @click="requestDeleteDepartmentImage(department)"
              >
                <span class="material-symbols-outlined text-lg">hide_image</span>
              </button>
              <button
                v-if="isAdminUser"
                type="button"
                class="h-9 w-9 rounded-full text-gray-400 hover:text-red-600 hover:bg-red-50 transition-colors flex items-center justify-center shrink-0"
                aria-label="Delete department profile"
                @click="requestDeleteDepartmentProfile(department)"
              >
                <span class="material-symbols-outlined text-lg">delete</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div
      v-if="showDepartmentImageCropper"
      class="fixed inset-0 z-[1120] bg-black/50 flex items-end sm:items-center justify-center"
      @click.self="cancelDepartmentImageCrop"
    >
      <div class="bg-white w-full sm:max-w-lg sm:rounded-lg max-h-[92vh] overflow-hidden flex flex-col text-left">
        <div class="p-4 border-b border-gray-100 flex items-center justify-between gap-3">
          <div class="min-w-0">
            <h2 class="text-lg font-semibold text-gray-900 truncate">Department Picture</h2>
            <p class="text-xs text-gray-500 truncate">{{ departmentImageTarget?.name || 'Department profile' }}</p>
          </div>
          <button
            @click="cancelDepartmentImageCrop"
            class="h-9 w-9 rounded-full bg-gray-100 text-gray-600 hover:bg-gray-200 flex items-center justify-center"
            aria-label="Close cropper"
          >
            <span class="material-symbols-outlined text-xl">close</span>
          </button>
        </div>

        <div class="p-4 space-y-4 overflow-y-auto">
          <div class="h-72 bg-blue-50 rounded-lg overflow-hidden">
            <img
              ref="departmentCropImage"
              :src="departmentImagePreview"
              alt="Department picture preview"
              class="w-full h-full object-contain"
            />
          </div>
          <div class="flex flex-col sm:flex-row gap-3">
            <button
              @click="cancelDepartmentImageCrop"
              class="flex-1 px-4 py-2.5 rounded-lg bg-gray-100 text-gray-700 hover:bg-gray-200 transition-colors"
            >
              Cancel
            </button>
            <button
              @click="confirmDepartmentImageCrop"
              :disabled="uploadingDepartmentImage || !departmentImageCropper"
              class="flex-1 px-4 py-2.5 rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 transition-colors inline-flex items-center justify-center gap-2"
            >
              <span v-if="uploadingDepartmentImage" class="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin"></span>
              Save Picture
            </button>
          </div>
        </div>
      </div>
    </div>

    <div
      v-if="showCreateDepartmentModal"
      class="fixed inset-0 z-[1100] bg-black/40 flex items-end sm:items-center justify-center"
      @click.self="closeCreateDepartmentModal"
    >
      <div class="bg-white w-full sm:max-w-lg sm:rounded-lg max-h-[92vh] overflow-hidden flex flex-col text-left">
        <div class="p-4 border-b border-gray-100 flex items-center justify-between gap-3">
          <div>
            <h2 class="text-lg font-semibold text-gray-900">New Department Profile</h2>
            <p class="text-xs text-gray-500">Verified profiles can be answered by assigned admins.</p>
          </div>
          <button
            @click="closeCreateDepartmentModal"
            class="h-9 w-9 rounded-full bg-gray-100 text-gray-600 hover:bg-gray-200 flex items-center justify-center"
            aria-label="Close"
          >
            <span class="material-symbols-outlined text-xl">close</span>
          </button>
        </div>

        <div class="p-4 space-y-4 overflow-y-auto">
          <label class="block">
            <span class="text-sm font-medium text-gray-700">Department Name</span>
            <input
              v-model.trim="departmentForm.name"
              type="text"
              maxlength="60"
              class="mt-1 w-full border border-gray-200 rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Student Finance, Registration..."
            />
          </label>

          <div class="rounded-lg border border-blue-100 bg-blue-50 p-3 flex items-center gap-2">
            <img :src="verifiedBadge" alt="Verified" class="h-5 w-5 shrink-0" />
            <p class="text-sm text-blue-900">Every department profile is verified automatically.</p>
          </div>

          <label class="block">
            <span class="text-sm font-medium text-gray-700">Assign Admin Responders</span>
            <div class="mt-1 relative">
              <span class="material-symbols-outlined text-gray-400 absolute left-3 top-1/2 -translate-y-1/2">search</span>
              <input
                v-model.trim="departmentForm.adminSearch"
                type="search"
                class="w-full border border-gray-200 rounded-lg pl-10 pr-4 py-3 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Search admins"
              />
            </div>
          </label>

          <div class="flex items-center justify-between text-sm">
            <span class="font-medium text-gray-700">Responders</span>
            <span class="text-gray-500">{{ departmentForm.selectedAdminIds.length }}/9 assigned</span>
          </div>

          <div class="p-3 flex items-center gap-3 border border-gray-100 rounded-lg bg-gray-50">
            <div class="h-10 w-10 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center overflow-hidden shrink-0">
              <img
                v-if="canShowAvatar('department-current-admin', currentUserSnapshot.profilePicture)"
                :src="currentUserSnapshot.profilePicture"
                :alt="currentUserSnapshot.name"
                class="h-full w-full object-cover"
                @error="markAvatarFailed('department-current-admin')"
              />
              <span v-else class="font-semibold">{{ initials(currentUserSnapshot.name) }}</span>
            </div>
            <div class="flex-1 min-w-0 text-left">
              <p class="font-medium text-gray-900 truncate">{{ currentUserSnapshot.name }}</p>
              <p class="text-xs text-gray-500 truncate">You are assigned automatically</p>
            </div>
          </div>

          <div v-if="loadingAdmins" class="flex items-center justify-center py-8">
            <div class="w-7 h-7 border-4 border-blue-200 border-t-blue-800 rounded-full animate-spin"></div>
          </div>

          <div v-else-if="filteredAdminProfiles.length === 0" class="text-center py-6">
            <span class="material-symbols-outlined text-4xl text-gray-300">admin_panel_settings</span>
            <p class="text-sm text-gray-500 mt-2">No other admins found.</p>
          </div>

          <div v-else class="divide-y divide-gray-100 border border-gray-100 rounded-lg overflow-hidden">
            <button
              v-for="admin in filteredAdminProfiles"
              :key="admin.id"
              type="button"
              class="w-full p-3 flex items-center gap-3 hover:bg-blue-50 transition-colors text-left"
              @click="toggleDepartmentAdmin(admin.id)"
            >
              <div class="h-10 w-10 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center overflow-hidden shrink-0">
                <img
                  v-if="canShowAvatar(adminAvatarKey(admin.id), admin.profilePicture)"
                  :src="admin.profilePicture"
                  :alt="admin.name"
                  class="h-full w-full object-cover"
                  @error="markAvatarFailed(adminAvatarKey(admin.id))"
                />
                <span v-else class="font-semibold">{{ initials(admin.name) }}</span>
              </div>
              <div class="flex-1 min-w-0 text-left">
                <p class="font-medium text-gray-900 truncate">{{ admin.name }}</p>
                <p class="text-xs text-gray-500 truncate">{{ admin.university?.name || 'Administrator' }}</p>
              </div>
              <span
                class="material-symbols-outlined text-2xl"
                :class="isSelectedDepartmentAdmin(admin.id) ? 'text-blue-600' : 'text-gray-300'"
              >
                {{ isSelectedDepartmentAdmin(admin.id) ? 'check_circle' : 'radio_button_unchecked' }}
              </span>
            </button>
          </div>
        </div>

        <div class="p-4 border-t border-gray-100 bg-white flex items-center justify-end gap-3">
          <button
            type="button"
            @click="closeCreateDepartmentModal"
            class="px-4 py-2.5 rounded-lg bg-gray-100 text-gray-700 hover:bg-gray-200 transition-colors"
          >
            Cancel
          </button>
          <button
            type="button"
            @click="createDepartment"
            :disabled="creatingDepartment || !departmentForm.name.trim()"
            class="px-4 py-2.5 rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors inline-flex items-center gap-2"
          >
            <span v-if="creatingDepartment" class="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin"></span>
            Create Profile
          </button>
        </div>
      </div>
    </div>

    <div
      v-if="showGroupModal"
      class="fixed inset-0 z-[1100] bg-black/40 flex items-end sm:items-center justify-center"
      @click.self="closeGroupModal"
    >
      <div class="bg-white w-full sm:max-w-lg sm:rounded-lg max-h-[92vh] overflow-hidden flex flex-col text-left">
        <div class="p-4 border-b border-gray-100 flex items-center justify-between gap-3">
          <div>
            <h2 class="text-lg font-semibold text-gray-900">New Group</h2>
            <p class="text-xs text-gray-500">Choose at least 2 connections.</p>
          </div>
          <button
            @click="closeGroupModal"
            class="h-9 w-9 rounded-full bg-gray-100 text-gray-600 hover:bg-gray-200 flex items-center justify-center"
            aria-label="Close"
          >
            <span class="material-symbols-outlined text-xl">close</span>
          </button>
        </div>

        <div class="p-4 space-y-4 overflow-y-auto">
          <label class="block">
            <span class="text-sm font-medium text-gray-700">Group Name</span>
            <input
              v-model.trim="groupForm.title"
              type="text"
              maxlength="50"
              class="mt-1 w-full border border-gray-200 rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Study group, project team..."
            />
          </label>

          <label class="block">
            <span class="text-sm font-medium text-gray-700">Find Connections</span>
            <div class="mt-1 relative">
              <span class="material-symbols-outlined text-gray-400 absolute left-3 top-1/2 -translate-y-1/2">search</span>
              <input
                v-model.trim="groupForm.search"
                type="search"
                class="w-full border border-gray-200 rounded-lg pl-10 pr-4 py-3 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Search by name"
              />
            </div>
          </label>

          <div class="flex items-center justify-between text-sm">
            <span class="font-medium text-gray-700">Members</span>
            <span class="text-gray-500">{{ groupForm.selectedIds.length }}/9 selected</span>
          </div>

          <div v-if="loadingConnections" class="flex items-center justify-center py-8">
            <div class="w-7 h-7 border-4 border-blue-200 border-t-blue-800 rounded-full animate-spin"></div>
          </div>

          <div v-else-if="filteredConnectionProfiles.length === 0" class="text-center py-8">
            <span class="material-symbols-outlined text-4xl text-gray-300">person_search</span>
            <p class="text-sm text-gray-500 mt-2">No matching connections found.</p>
          </div>

          <div v-else class="divide-y divide-gray-100 border border-gray-100 rounded-lg overflow-hidden">
            <button
              v-for="connection in filteredConnectionProfiles"
              :key="connection.id"
              type="button"
              class="w-full p-3 flex items-center gap-3 hover:bg-blue-50 transition-colors text-left"
              @click="toggleGroupMember(connection.id)"
            >
              <div class="h-10 w-10 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center overflow-hidden shrink-0">
                <img
                  v-if="canShowAvatar(connectionAvatarKey(connection.id), connection.profilePicture)"
                  :src="connection.profilePicture"
                  :alt="connection.name"
                  class="h-full w-full object-cover"
                  @error="markAvatarFailed(connectionAvatarKey(connection.id))"
                />
                <span v-else class="font-semibold">{{ initials(connection.name) }}</span>
              </div>
              <div class="flex-1 min-w-0 text-left">
                <p class="font-medium text-gray-900 truncate">{{ connection.name }}</p>
                <p class="text-xs text-gray-500 truncate">{{ connection.university?.name || 'Connection' }}</p>
              </div>
              <span
                class="material-symbols-outlined text-2xl"
                :class="isSelectedForGroup(connection.id) ? 'text-blue-600' : 'text-gray-300'"
              >
                {{ isSelectedForGroup(connection.id) ? 'check_circle' : 'radio_button_unchecked' }}
              </span>
            </button>
          </div>
        </div>

        <div class="p-4 border-t border-gray-100 bg-white flex items-center justify-end gap-3">
          <button
            type="button"
            @click="closeGroupModal"
            class="px-4 py-2.5 rounded-lg bg-gray-100 text-gray-700 hover:bg-gray-200 transition-colors"
          >
            Cancel
          </button>
          <button
            type="button"
            @click="createGroupChat"
            :disabled="creatingGroup || groupForm.selectedIds.length < 2 || !groupForm.title.trim()"
            class="px-4 py-2.5 rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors inline-flex items-center gap-2"
          >
            <span v-if="creatingGroup" class="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin"></span>
            Create Group
          </button>
        </div>
      </div>
    </div>

    <div
      v-if="showDetailsPanel && activeConversation"
      class="fixed inset-0 z-[1090] bg-black/40 flex items-end lg:items-stretch lg:justify-end"
      @click.self="closeDetailsPanel"
    >
      <aside class="bg-white w-full lg:w-[420px] max-h-[92vh] lg:max-h-none overflow-hidden flex flex-col rounded-t-lg lg:rounded-none text-left">
        <div class="p-4 border-b border-gray-100 flex items-center justify-between gap-3">
          <div>
            <h2 class="text-lg font-semibold text-gray-900">
              {{ activeConversationIsDepartment ? 'Department Details' : 'Group Details' }}
            </h2>
            <p class="text-xs text-gray-500">{{ activeConversationSubtitle }}</p>
          </div>
          <button
            @click="closeDetailsPanel"
            class="h-9 w-9 rounded-full bg-gray-100 text-gray-600 hover:bg-gray-200 flex items-center justify-center"
            :aria-label="activeConversationIsDepartment ? 'Close department details' : 'Close group details'"
          >
            <span class="material-symbols-outlined text-xl">close</span>
          </button>
        </div>

        <div class="flex-1 overflow-y-auto p-4 space-y-5">
          <section class="text-center">
            <div class="relative mx-auto h-24 w-24 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center overflow-hidden">
              <img
                v-if="canShowAvatar(activeAvatarKey, activeConversationAvatar)"
                :src="activeConversationAvatar"
                :alt="activeConversationTitle"
                class="h-full w-full object-cover"
                @error="markAvatarFailed(activeAvatarKey)"
              />
              <span v-else class="material-symbols-outlined text-4xl">
                {{ activeConversationIsDepartment ? 'account_balance' : 'groups' }}
              </span>
            </div>
            <div class="mt-3 flex items-center justify-center gap-1.5 min-w-0">
              <h3 class="text-xl font-semibold text-gray-900 truncate">{{ activeConversationTitle }}</h3>
              <img
                v-if="activeConversationIsDepartment"
                :src="verifiedBadge"
                alt="Verified"
                class="h-5 w-5 shrink-0"
              />
            </div>
            <p class="text-sm text-gray-500">
              {{ activeConversationIsDepartment ? (activeDepartmentDescription || 'Verified department profile.') : (activeConversation.description || 'No description yet.') }}
            </p>
            <p v-if="isGroupAdmin" class="mt-2 text-xs font-semibold text-blue-700">You are the group admin</p>
            <p v-if="isDepartmentResponder" class="mt-2 text-xs font-semibold text-blue-700">You can respond as this department</p>
          </section>

          <section v-if="isGroupAdmin" class="border border-gray-100 rounded-lg p-4 space-y-4">
            <div class="flex items-center justify-between">
              <h4 class="font-semibold text-gray-900">Edit Group</h4>
              <button
                @click="toggleGroupEdit"
                class="text-sm font-medium text-blue-700 hover:text-blue-900"
              >
                {{ isEditingGroup ? 'Cancel' : 'Edit' }}
              </button>
            </div>

            <div v-if="isEditingGroup" class="space-y-3">
              <label class="block">
                <span class="text-sm font-medium text-gray-700">Group Name</span>
                <input
                  v-model.trim="detailsForm.title"
                  type="text"
                  maxlength="50"
                  class="mt-1 w-full border border-gray-200 rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </label>
              <label class="block">
                <span class="text-sm font-medium text-gray-700">Description</span>
                <textarea
                  v-model.trim="detailsForm.description"
                  maxlength="180"
                  rows="3"
                  class="mt-1 w-full border border-gray-200 rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                  placeholder="What is this group for?"
                ></textarea>
              </label>
              <button
                @click="saveGroupDetails"
                :disabled="savingGroupDetails || !detailsForm.title.trim()"
                class="w-full px-4 py-2.5 rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors inline-flex items-center justify-center gap-2"
              >
                <span v-if="savingGroupDetails" class="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin"></span>
                Save Changes
              </button>
            </div>

            <div class="space-y-3">
              <label class="block">
                <span class="text-sm font-medium text-gray-700">Group Picture</span>
                <input
                  ref="groupPictureInput"
                  type="file"
                  accept="image/*"
                  @change="handleGroupImageFile"
                  :disabled="uploadingGroupImage"
                  class="mt-1 block w-full text-sm text-blue-900 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-900 hover:file:bg-blue-100 disabled:opacity-50"
                />
              </label>
              <button
                v-if="activeConversation.groupProfilePicture"
                @click="requestDeleteGroupImage"
                :disabled="uploadingGroupImage"
                class="w-full px-4 py-2.5 rounded-lg bg-red-50 text-red-700 hover:bg-red-100 disabled:opacity-50 transition-colors inline-flex items-center justify-center gap-2"
              >
                <span class="material-symbols-outlined text-lg">delete</span>
                Remove Group Picture
              </button>
            </div>
          </section>

          <section v-if="showImageCropper" class="border border-blue-100 rounded-lg p-4 space-y-4">
            <h4 class="font-semibold text-gray-900">Crop Picture</h4>
            <div class="h-72 bg-blue-50 rounded-lg overflow-hidden">
              <img
                ref="groupCropImage"
                :src="groupImagePreview"
                alt="Group picture preview"
                class="w-full h-full object-contain"
              />
            </div>
            <div class="flex flex-col sm:flex-row gap-3">
              <button
                @click="cancelGroupImageCrop"
                class="flex-1 px-4 py-2.5 rounded-lg bg-gray-100 text-gray-700 hover:bg-gray-200 transition-colors"
              >
                Cancel
              </button>
              <button
                @click="confirmGroupImageCrop"
                :disabled="uploadingGroupImage || !groupImageCropper"
                class="flex-1 px-4 py-2.5 rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 transition-colors inline-flex items-center justify-center gap-2"
              >
                <span v-if="uploadingGroupImage" class="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin"></span>
                Save Picture
              </button>
            </div>
          </section>

          <section v-if="activeConversationIsDepartment && isAdminUser" class="border border-gray-100 rounded-lg p-4 space-y-4">
            <div class="flex items-start justify-between gap-3">
              <div class="text-left">
                <h4 class="font-semibold text-gray-900">Manage Department</h4>
                <p class="text-xs text-gray-500 mt-1">Update the verified profile that students see in chat.</p>
              </div>
              <button
                @click="toggleDepartmentEdit"
                class="text-sm font-medium text-blue-700 hover:text-blue-900"
              >
                {{ isEditingDepartment ? 'Cancel' : 'Edit' }}
              </button>
            </div>

            <div v-if="isEditingDepartment" class="space-y-3">
              <label class="block">
                <span class="text-sm font-medium text-gray-700">Department Name</span>
                <input
                  v-model.trim="detailsForm.title"
                  type="text"
                  maxlength="60"
                  class="mt-1 w-full border border-gray-200 rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Department name"
                />
              </label>
              <label class="block">
                <span class="text-sm font-medium text-gray-700">Description</span>
                <textarea
                  v-model.trim="detailsForm.description"
                  maxlength="180"
                  rows="3"
                  class="mt-1 w-full border border-gray-200 rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                  placeholder="What should students know about this department?"
                ></textarea>
              </label>
              <button
                @click="saveDepartmentDetails"
                :disabled="savingDepartmentDetails || !detailsForm.title.trim()"
                class="w-full px-4 py-2.5 rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors inline-flex items-center justify-center gap-2"
              >
                <span v-if="savingDepartmentDetails" class="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin"></span>
                Save Changes
              </button>
            </div>

            <div v-else class="text-left">
              <p class="text-sm text-gray-700 font-medium">{{ activeConversationTitle }}</p>
              <p class="text-xs text-gray-500 mt-1">{{ activeDepartmentDescription || 'No description yet.' }}</p>
            </div>

            <label class="block text-left">
              <span class="text-sm font-medium text-gray-700 text-left block">Department Picture</span>
              <input
                type="file"
                accept="image/*"
                @change="handleDepartmentImageFile(activeDepartmentProfileForDetails, $event)"
                :disabled="uploadingDepartmentImage"
                class="mt-1 block w-full text-sm text-blue-900 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-900 hover:file:bg-blue-100 disabled:opacity-50"
              />
            </label>
            <button
              v-if="activeConversationAvatar"
              @click="requestDeleteDepartmentImage(activeDepartmentProfileForDetails)"
              :disabled="uploadingDepartmentImage"
              class="w-full px-4 py-2.5 rounded-lg bg-red-50 text-red-700 hover:bg-red-100 disabled:opacity-50 transition-colors inline-flex items-center justify-center gap-2"
            >
              <span class="material-symbols-outlined text-lg">hide_image</span>
              Remove Department Picture
            </button>
          </section>

          <section v-if="activeConversationIsGroup">
            <div class="flex items-center justify-between mb-3">
              <h4 class="font-semibold text-gray-900">Members</h4>
              <span class="text-sm text-gray-500">{{ groupMembers.length }}</span>
            </div>
            <div class="divide-y divide-gray-100 border border-gray-100 rounded-lg overflow-hidden">
              <div
                v-for="member in groupMembers"
                :key="member.id"
                class="p-3 flex items-center gap-3"
              >
                <div class="h-10 w-10 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center overflow-hidden shrink-0">
                  <img
                    v-if="canShowAvatar(memberAvatarKey(member.id), member.profilePicture)"
                    :src="member.profilePicture"
                    :alt="member.name"
                    class="h-full w-full object-cover"
                    @error="markAvatarFailed(memberAvatarKey(member.id))"
                  />
                  <span v-else class="font-semibold">{{ initials(member.name) }}</span>
                </div>
                <div class="flex-1 min-w-0 text-left">
                  <div class="flex items-center gap-2 min-w-0">
                    <p class="font-medium text-gray-900 truncate">{{ member.name }}</p>
                    <span
                      v-if="member.id === activeConversation.createdBy"
                      class="text-[11px] font-semibold text-blue-700 bg-blue-50 rounded-full px-2 py-0.5"
                    >
                      Admin
                    </span>
                  </div>
                  <p class="text-xs text-gray-500 truncate">{{ member.university?.name || 'Member' }}</p>
                </div>
                <button
                  v-if="isGroupAdmin && member.id !== activeConversation.createdBy"
                  @click="requestRemoveMember(member)"
                  class="h-9 w-9 rounded-full text-gray-400 hover:text-red-600 hover:bg-red-50 flex items-center justify-center transition-colors"
                  aria-label="Remove member"
                >
                  <span class="material-symbols-outlined text-lg">person_remove</span>
                </button>
              </div>
            </div>
          </section>

          <section v-if="activeConversationIsGroup" class="pt-1">
            <button
              v-if="canDeleteActiveConversation"
              @click="requestDeleteConversation(activeConversation)"
              class="w-full px-4 py-3 rounded-lg bg-red-600 text-white hover:bg-red-700 transition-colors inline-flex items-center justify-center gap-2"
            >
              <span class="material-symbols-outlined text-lg">delete</span>
              Delete Group
            </button>
          </section>

          <section v-if="activeConversationIsDepartment && isAdminUser" class="pt-1">
            <button
              @click="requestDeleteDepartmentProfile(activeDepartmentProfileForDetails)"
              class="w-full px-4 py-3 rounded-lg bg-red-600 text-white hover:bg-red-700 transition-colors inline-flex items-center justify-center gap-2"
            >
              <span class="material-symbols-outlined text-lg">delete</span>
              Delete Department Profile
            </button>
          </section>
        </div>
      </aside>
    </div>

    <app-confirm-dialog
      v-model="confirmDialog.visible"
      :title="confirmDialog.title"
      :message="confirmDialog.message"
      :type="confirmDialog.type"
      :icon="confirmDialog.icon"
      :confirm-text="confirmDialog.confirmText"
      @confirm="handleConfirmAction"
    />
  </div>
</template>

<script>
import { mapState, mapActions } from 'vuex';
import ConversationItem from '@/components/chat/ConversationItem.vue';
import MessageList from '@/components/chat/MessageList.vue';
import MessageInput from '@/components/chat/MessageInput.vue';
import AppConfirmDialog from '@/components/common/AppConfirmDialog.vue';
import {
  compactParticipant,
  createDirectConversation,
  createDepartmentProfile,
  createGroupConversation,
  createOrGetDepartmentConversation,
  deleteConversation as deleteConversationFromFirestore,
  deleteDepartmentProfile,
  deleteDepartmentProfilePicture,
  deleteGroupProfilePicture,
  getAdminProfiles,
  getConversationAvatar,
  getConversationTitle,
  getConversationType,
  getDepartmentProfiles,
  removeGroupMember,
  subscribeToConversations,
  subscribeToMessages,
  updateDepartmentProfile,
  updateGroupDetails,
  uploadDepartmentProfilePicture,
  uploadGroupProfilePicture,
  sendMessage as sendMessageToFirestore
} from '@/composables/useChat';
import { getUserDataBatch, getUserDetails } from '@/composables/useFirestore';
import verifiedBadge from '@/assets/verifiedBadge.png';

export default {
  components: {
    ConversationItem,
    MessageList,
    MessageInput,
    AppConfirmDialog
  },
  data() {
    return {
      activeConversation: null,
      conversations: [],
      messages: [],
      chatsLoading: true,
      loadingMessages: false,
      sendingMessage: false,
      conversationsUnsubscribe: null,
      messagesUnsubscribe: null,
      showGroupModal: false,
      showDepartmentModal: false,
      showCreateDepartmentModal: false,
      creatingGroup: false,
      creatingDepartment: false,
      loadingDepartments: false,
      loadingAdmins: false,
      loadingConnections: false,
      connectionProfileCache: {},
      departmentProfiles: [],
      departmentProfilesLoaded: false,
      adminProfiles: [],
      adminProfilesLoaded: false,
      failedAvatarKeys: {},
      showDetailsPanel: false,
      isEditingGroup: false,
      isEditingDepartment: false,
      savingGroupDetails: false,
      savingDepartmentDetails: false,
      uploadingGroupImage: false,
      groupImagePreview: '',
      groupImageCropper: null,
      uploadingDepartmentImage: false,
      departmentImagePreview: '',
      departmentImageCropper: null,
      departmentImageTarget: null,
      detailsForm: {
        title: '',
        description: ''
      },
      confirmDialog: {
        visible: false,
        title: '',
        message: '',
        type: 'danger',
        icon: 'warning',
        confirmText: 'Confirm',
        action: null,
        payload: null
      },
      groupForm: {
        title: '',
        search: '',
        selectedIds: []
      },
      departmentSearch: '',
      departmentForm: {
        name: '',
        adminSearch: '',
        selectedAdminIds: []
      },
      verifiedBadge,
      isMobileView: window.innerWidth < 768
    };
  },
  computed: {
    ...mapState(['user', 'profile', 'connections', 'socialUsers']),
    userId() {
      return this.user?.uid;
    },
    hasConnections() {
      return this.connections && this.connections.length > 0;
    },
    isAdminUser() {
      return this.profile?.role === 'admin';
    },
    currentUserSnapshot() {
      return compactParticipant({
        id: this.userId,
        name: this.profile?.name || this.user?.displayName || this.user?.email || 'Student',
        profilePicture: this.profile?.profilePicture || '',
        university: this.profile?.university || {}
      });
    },
    activeConversationIsGroup() {
      return this.activeConversation && getConversationType(this.activeConversation) === 'group';
    },
    activeConversationIsDepartment() {
      return this.activeConversation && getConversationType(this.activeConversation) === 'department';
    },
    activeConversationSupportsDetails() {
      return this.activeConversationIsGroup || this.activeConversationIsDepartment;
    },
    isGroupAdmin() {
      return this.activeConversationIsGroup && this.activeConversation?.createdBy === this.userId;
    },
    isDepartmentResponder() {
      return this.activeConversationIsDepartment &&
        (this.activeConversation.assignedAdminIds || []).includes(this.userId);
    },
    activeOwnedSenderIds() {
      const ownedIds = [this.userId];
      if (this.isDepartmentResponder && this.activeConversation?.departmentProfileId) {
        ownedIds.push(this.activeConversation.departmentProfileId);
      }
      return ownedIds;
    },
    canDeleteActiveConversation() {
      return this.canDeleteConversation(this.activeConversation);
    },
    activeConversationTitle() {
      return getConversationTitle(this.activeConversation, this.userId);
    },
    activeConversationAvatar() {
      return getConversationAvatar(this.activeConversation, this.userId);
    },
    activeAvatarKey() {
      return `conversation-${this.activeConversation?.id || 'active'}`;
    },
    activeDepartmentProfileForDetails() {
      if (!this.activeConversationIsDepartment) return null;

      const snapshot = this.activeConversation.departmentProfileSnapshot || {};
      return {
        id: this.activeConversation.departmentProfileId,
        name: snapshot.name || this.activeConversationTitle,
        description: snapshot.description || '',
        profilePicture: snapshot.profilePicture || this.activeConversationAvatar,
        profilePicturePath: snapshot.profilePicturePath || ''
      };
    },
    activeDepartmentDescription() {
      return this.activeConversation?.departmentProfileSnapshot?.description || '';
    },
    showImageCropper() {
      return Boolean(this.groupImagePreview);
    },
    showDepartmentImageCropper() {
      return Boolean(this.departmentImagePreview);
    },
    activeConversationSubtitle() {
      if (!this.activeConversation) return '';

      if (this.activeConversationIsDepartment) {
        return this.isDepartmentResponder ? 'Verified department inbox' : 'Verified department profile';
      }

      if (this.activeConversationIsGroup) {
        const count = this.activeConversation.participants?.length || 0;
        return `${count} member${count === 1 ? '' : 's'}`;
      }

      const otherUser = (this.activeConversation.participantDetails || []).find(
        participant => participant.id !== this.userId
      );
      return otherUser?.university?.name || 'Direct message';
    },
    groupMembers() {
      if (!this.activeConversationIsGroup) return [];

      const detailsById = (this.activeConversation.participantDetails || []).reduce((lookup, member) => {
        if (member?.id) lookup[member.id] = member;
        return lookup;
      }, {});

      return (this.activeConversation.participants || []).map((memberId) => {
        return detailsById[memberId] || {
          id: memberId,
          name: 'Student',
          profilePicture: '',
          university: {}
        };
      });
    },
    connectionProfiles() {
      return (this.connections || [])
        .map((connectionId) => {
          const cachedProfile = this.connectionProfileCache[connectionId];
          const socialProfile = (this.socialUsers || []).find(user => user.id === connectionId);
          return cachedProfile || (socialProfile ? compactParticipant(socialProfile) : null);
        })
        .filter(Boolean)
        .sort((a, b) => a.name.localeCompare(b.name));
    },
    filteredConnectionProfiles() {
      const search = this.groupForm.search.toLowerCase();
      if (!search) return this.connectionProfiles;

      return this.connectionProfiles.filter((connection) => {
        return connection.name.toLowerCase().includes(search);
      });
    },
    filteredDepartmentProfiles() {
      const search = this.departmentSearch.toLowerCase();
      const profiles = [...this.departmentProfiles].sort((a, b) => {
        return (a.name || '').localeCompare(b.name || '');
      });

      if (!search) return profiles;
      return profiles.filter(profile => (profile.name || '').toLowerCase().includes(search));
    },
    filteredAdminProfiles() {
      const search = this.departmentForm.adminSearch.toLowerCase();
      const profiles = this.adminProfiles.filter(admin => admin.id !== this.userId);

      if (!search) return profiles;
      return profiles.filter(admin => admin.name.toLowerCase().includes(search));
    },
    displayConversations() {
      return this.conversations.filter((conversation) => {
        if (!this.isAdminSelfDepartmentConversation(conversation)) {
          return true;
        }

        return !this.conversations.some((otherConversation) => {
          return getConversationType(otherConversation) === 'department' &&
            otherConversation.departmentProfileId === conversation.departmentProfileId &&
            otherConversation.id !== conversation.id &&
            otherConversation.studentId !== this.userId &&
            (otherConversation.assignedAdminIds || []).includes(this.userId);
        });
      });
    }
  },
  methods: {
    ...mapActions(['setSelectedConversation', 'clearSelectedConversation']),
    isAdminSelfDepartmentConversation(conversation) {
      return this.isAdminUser &&
        getConversationType(conversation) === 'department' &&
        conversation.studentId === this.userId &&
        (conversation.assignedAdminIds || []).includes(this.userId);
    },
    async loadConversations() {
      if (!this.userId) return;

      this.chatsLoading = true;
      if (this.conversationsUnsubscribe) {
        this.conversationsUnsubscribe();
        this.conversationsUnsubscribe = null;
      }

      this.conversationsUnsubscribe = subscribeToConversations(this.userId, (conversations) => {
        this.conversations = conversations;
        this.chatsLoading = false;

        if (this.activeConversation) {
          const updatedConversation = conversations.find(
            conversation => conversation.id === this.activeConversation.id
          );
          if (updatedConversation) {
            this.activeConversation = updatedConversation;
            this.setSelectedConversation(updatedConversation);
          }
        }

        if (!this.activeConversation && this.$route.query.conversation) {
          const routeConversation = conversations.find(
            conversation => conversation.id === this.$route.query.conversation
          );
          if (routeConversation) {
            this.selectConversation(routeConversation, { keepRoute: true });
          }
        }
      });
    },
    async loadConversationWithUser(otherUserId) {
      if (!otherUserId || !this.userId) return;

      if (!this.connections.includes(otherUserId)) {
        this.$store.dispatch('showAlert', {
          message: 'You can only chat with your connections.',
          type: 'error'
        });
        this.$router.replace('/chat');
        return;
      }

      try {
        const otherUser = await this.getConnectionProfile(otherUserId);
        const result = await createDirectConversation(
          [this.userId, otherUserId],
          [this.currentUserSnapshot, otherUser]
        );

        if (!result.success) {
          throw new Error(result.message);
        }

        const conversation = result.conversation || {
          id: result.conversationId,
          type: 'direct',
          participants: [this.userId, otherUserId],
          participantDetails: [this.currentUserSnapshot, otherUser]
        };

        await this.selectConversation(conversation, { keepRoute: true });
      } catch (error) {
        console.error('Error loading direct conversation:', error);
        this.$store.dispatch('showAlert', {
          message: 'Failed to load conversation. Please try again.',
          type: 'error'
        });
      }
    },
    async selectConversation(conversation, options = {}) {
      if (!conversation?.id) return;

      this.activeConversation = conversation;
      this.setSelectedConversation(conversation);

      if (!options.keepRoute && this.$route.params.userId) {
        this.$router.replace('/chat');
      }

      await this.setupMessageListener(conversation.id);
    },
    async setupMessageListener(conversationId) {
      this.loadingMessages = true;
      this.messages = [];

      if (this.messagesUnsubscribe) {
        this.messagesUnsubscribe();
        this.messagesUnsubscribe = null;
      }

      this.messagesUnsubscribe = subscribeToMessages(conversationId, (messages) => {
        this.messages = messages;
        this.loadingMessages = false;
      });

      if (!this.messagesUnsubscribe) {
        this.loadingMessages = false;
      }
    },
    async sendMessage(messageText) {
      if (!messageText.trim() || !this.activeConversation) return;

      this.sendingMessage = true;
      try {
        const isDepartmentReply = this.activeConversationIsDepartment && this.isDepartmentResponder;
        const departmentSnapshot = this.activeConversation.departmentProfileSnapshot || {};
        const result = await sendMessageToFirestore({
          conversationId: this.activeConversation.id,
          senderId: isDepartmentReply ? this.activeConversation.departmentProfileId : this.userId,
          senderName: isDepartmentReply
            ? departmentSnapshot.name || this.activeConversationTitle
            : this.currentUserSnapshot.name,
          text: messageText,
          senderType: isDepartmentReply ? 'department' : 'user',
          sentBy: isDepartmentReply ? this.userId : null,
          departmentProfileId: isDepartmentReply ? this.activeConversation.departmentProfileId : null
        });

        if (!result.success) {
          throw new Error(result.message);
        }
      } catch (error) {
        console.error('Error sending message:', error);
        this.$store.dispatch('showAlert', {
          message: 'Failed to send message. Please try again.',
          type: 'error'
        });
      } finally {
        this.sendingMessage = false;
      }
    },
    async openDepartmentModal() {
      this.departmentSearch = '';
      this.showDepartmentModal = true;
      await this.ensureDepartmentProfiles();
    },
    closeDepartmentModal() {
      if (this.loadingDepartments) return;
      this.showDepartmentModal = false;
      this.cancelDepartmentImageCrop();
    },
    async ensureDepartmentProfiles(force = false) {
      if (this.departmentProfilesLoaded && !force) return;

      this.loadingDepartments = true;
      try {
        const result = await getDepartmentProfiles();
        if (!result.success) {
          throw new Error(result.message);
        }
        this.departmentProfiles = result.departmentProfiles;
        this.departmentProfilesLoaded = true;
      } catch (error) {
        console.error('Error loading departments:', error);
        this.$store.dispatch('showAlert', {
          message: 'Could not load department profiles.',
          type: 'error'
        });
      } finally {
        this.loadingDepartments = false;
      }
    },
    async openDepartmentConversation(departmentProfile) {
      if (!departmentProfile?.id || !this.userId) return;

      try {
        const departmentConversations = this.conversations.filter((conversation) => {
          return getConversationType(conversation) === 'department' &&
            conversation.departmentProfileId === departmentProfile.id;
        });
        const isAssignedDepartmentAdmin = (departmentProfile.assignedAdminIds || []).includes(this.userId);

        if (this.isAdminUser && isAssignedDepartmentAdmin) {
          const existingStudentConversation = departmentConversations.find((conversation) => {
            return conversation.studentId !== this.userId &&
              (conversation.assignedAdminIds || []).includes(this.userId);
          });

          if (existingStudentConversation) {
            this.showDepartmentModal = false;
            await this.selectConversation(existingStudentConversation);
            return;
          }

          this.showDepartmentModal = false;
          this.$store.dispatch('showAlert', {
            message: 'No student messages for this department yet.',
            type: 'info'
          });
          return;
        }

        const existingConversation = departmentConversations.find((conversation) => {
          return conversation.studentId === this.userId;
        });

        if (existingConversation) {
          this.showDepartmentModal = false;
          await this.selectConversation(existingConversation);
          return;
        }

        const result = await createOrGetDepartmentConversation({
          departmentProfile,
          studentId: this.userId,
          studentSnapshot: this.currentUserSnapshot
        });

        if (!result.success) {
          throw new Error(result.message);
        }

        this.showDepartmentModal = false;
        await this.selectConversation(result.conversation);
      } catch (error) {
        console.error('Error opening department conversation:', error);
        this.$store.dispatch('showAlert', {
          message: error.message || 'Failed to open department chat.',
          type: 'error'
        });
      }
    },
    async openCreateDepartmentModal() {
      if (!this.isAdminUser) return;

      this.departmentForm = {
        name: '',
        adminSearch: '',
        selectedAdminIds: [this.userId]
      };
      this.showCreateDepartmentModal = true;
      await this.ensureAdminProfiles();
    },
    closeCreateDepartmentModal() {
      if (this.creatingDepartment) return;
      this.showCreateDepartmentModal = false;
    },
    async ensureAdminProfiles(force = false) {
      if (this.adminProfilesLoaded && !force) return;

      this.loadingAdmins = true;
      try {
        const result = await getAdminProfiles();
        if (!result.success) {
          throw new Error(result.message);
        }

        const adminMap = result.admins.reduce((lookup, admin) => {
          lookup[admin.id] = admin;
          return lookup;
        }, {});
        adminMap[this.userId] = this.currentUserSnapshot;

        this.adminProfiles = Object.values(adminMap).sort((a, b) => a.name.localeCompare(b.name));
        this.adminProfilesLoaded = true;
      } catch (error) {
        console.error('Error loading admins:', error);
        this.$store.dispatch('showAlert', {
          message: 'Could not load admins for department profile.',
          type: 'error'
        });
      } finally {
        this.loadingAdmins = false;
      }
    },
    toggleDepartmentAdmin(adminId) {
      if (adminId === this.userId) return;

      if (this.isSelectedDepartmentAdmin(adminId)) {
        this.departmentForm.selectedAdminIds = this.departmentForm.selectedAdminIds.filter(id => id !== adminId);
        return;
      }

      if (this.departmentForm.selectedAdminIds.length >= 9) {
        this.$store.dispatch('showAlert', {
          message: 'Department profiles can have up to 9 assigned admins.',
          type: 'info'
        });
        return;
      }

      this.departmentForm.selectedAdminIds = [...this.departmentForm.selectedAdminIds, adminId];
    },
    isSelectedDepartmentAdmin(adminId) {
      return this.departmentForm.selectedAdminIds.includes(adminId);
    },
    async createDepartment() {
      if (!this.isAdminUser) return;

      if (!this.departmentForm.name.trim()) {
        this.$store.dispatch('showAlert', {
          message: 'Please enter a department name.',
          type: 'error'
        });
        return;
      }

      this.creatingDepartment = true;
      try {
        const result = await createDepartmentProfile({
          name: this.departmentForm.name,
          assignedAdminIds: this.departmentForm.selectedAdminIds,
          createdBy: this.userId
        });

        if (!result.success) {
          throw new Error(result.message);
        }

        const departmentProfile = result.departmentProfile;
        if (departmentProfile?.id) {
          const existingIndex = this.departmentProfiles.findIndex(profile => profile.id === departmentProfile.id);
          if (existingIndex >= 0) {
            this.departmentProfiles.splice(existingIndex, 1, departmentProfile);
          } else {
            this.departmentProfiles = [...this.departmentProfiles, departmentProfile];
          }
          this.departmentProfilesLoaded = true;
        }

        this.showCreateDepartmentModal = false;
        this.$store.dispatch('showAlert', {
          message: result.message || 'Department profile created.',
          type: result.message ? 'info' : 'success'
        });
      } catch (error) {
        console.error('Error creating department:', error);
        this.$store.dispatch('showAlert', {
          message: error.message || 'Failed to create department profile.',
          type: 'error'
        });
      } finally {
        this.creatingDepartment = false;
      }
    },
    async openGroupModal() {
      this.groupForm = {
        title: '',
        search: '',
        selectedIds: []
      };
      this.showGroupModal = true;
      await this.ensureConnectionProfiles();
    },
    closeGroupModal() {
      if (this.creatingGroup) return;
      this.showGroupModal = false;
    },
    async ensureConnectionProfiles() {
      const missingIds = (this.connections || []).filter((connectionId) => {
        return !this.connectionProfileCache[connectionId] &&
          !(this.socialUsers || []).some(user => user.id === connectionId);
      });

      if (!missingIds.length) return;

      this.loadingConnections = true;
      try {
        const result = await getUserDataBatch(missingIds);
        if (result.success) {
          const nextCache = { ...this.connectionProfileCache };
          result.users.forEach((user) => {
            nextCache[user.id] = compactParticipant(user);
          });
          this.connectionProfileCache = nextCache;
        }
      } catch (error) {
        console.error('Error loading connection profiles:', error);
        this.$store.dispatch('showAlert', {
          message: 'Could not load connections for group chat.',
          type: 'error'
        });
      } finally {
        this.loadingConnections = false;
      }
    },
    async getConnectionProfile(userId) {
      const cachedProfile = this.connectionProfileCache[userId];
      if (cachedProfile) return cachedProfile;

      const socialProfile = (this.socialUsers || []).find(user => user.id === userId);
      if (socialProfile) {
        const compact = compactParticipant(socialProfile);
        this.connectionProfileCache = {
          ...this.connectionProfileCache,
          [userId]: compact
        };
        return compact;
      }

      const result = await getUserDetails(userId);
      if (result.success && result.userData) {
        const compact = compactParticipant(result.userData);
        this.connectionProfileCache = {
          ...this.connectionProfileCache,
          [userId]: compact
        };
        return compact;
      }

      return { id: userId, name: 'Student', profilePicture: '', university: {} };
    },
    toggleGroupMember(userId) {
      if (this.isSelectedForGroup(userId)) {
        this.groupForm.selectedIds = this.groupForm.selectedIds.filter(id => id !== userId);
        return;
      }

      if (this.groupForm.selectedIds.length >= 9) {
        this.$store.dispatch('showAlert', {
          message: 'Groups can include up to 10 people including you.',
          type: 'info'
        });
        return;
      }

      this.groupForm.selectedIds = [...this.groupForm.selectedIds, userId];
    },
    isSelectedForGroup(userId) {
      return this.groupForm.selectedIds.includes(userId);
    },
    async createGroupChat() {
      if (!this.groupForm.title.trim()) {
        this.$store.dispatch('showAlert', {
          message: 'Please enter a group name.',
          type: 'error'
        });
        return;
      }

      if (this.groupForm.selectedIds.length < 2) {
        this.$store.dispatch('showAlert', {
          message: 'Choose at least 2 connections.',
          type: 'error'
        });
        return;
      }

      this.creatingGroup = true;
      try {
        const selectedProfiles = await Promise.all(
          this.groupForm.selectedIds.map(userId => this.getConnectionProfile(userId))
        );
        const participants = [this.userId, ...this.groupForm.selectedIds];
        const result = await createGroupConversation({
          title: this.groupForm.title,
          participants,
          participantDetails: [this.currentUserSnapshot, ...selectedProfiles],
          createdBy: this.userId
        });

        if (!result.success) {
          throw new Error(result.message);
        }

        this.showGroupModal = false;
        await this.selectConversation(result.conversation);
        this.$store.dispatch('showAlert', {
          message: 'Group chat created.',
          type: 'success'
        });
      } catch (error) {
        console.error('Error creating group chat:', error);
        this.$store.dispatch('showAlert', {
          message: error.message || 'Failed to create group chat.',
          type: 'error'
        });
      } finally {
        this.creatingGroup = false;
      }
    },
    canDeleteConversation(conversation) {
      if (!conversation || !this.userId || !(conversation.participants || []).includes(this.userId)) {
        return false;
      }

      const type = getConversationType(conversation);
      return type === 'direct' || (type === 'group' && conversation.createdBy === this.userId);
    },
    openDetailsPanel() {
      if (!this.activeConversationSupportsDetails) return;

      this.detailsForm = {
        title: this.activeConversationIsDepartment
          ? this.activeConversationTitle
          : this.activeConversation.title || '',
        description: this.activeConversationIsDepartment
          ? this.activeDepartmentDescription
          : this.activeConversation.description || ''
      };
      this.isEditingGroup = false;
      this.isEditingDepartment = false;
      this.showDetailsPanel = true;
    },
    closeDetailsPanel() {
      this.showDetailsPanel = false;
      this.isEditingGroup = false;
      this.isEditingDepartment = false;
      this.cancelGroupImageCrop();
      this.cancelDepartmentImageCrop();
    },
    toggleGroupEdit() {
      this.isEditingGroup = !this.isEditingGroup;
      this.detailsForm = {
        title: this.activeConversation?.title || '',
        description: this.activeConversation?.description || ''
      };
    },
    toggleDepartmentEdit() {
      this.isEditingDepartment = !this.isEditingDepartment;
      this.detailsForm = {
        title: this.activeConversationTitle || '',
        description: this.activeDepartmentDescription || ''
      };
    },
    async saveGroupDetails() {
      if (!this.activeConversation?.id || !this.detailsForm.title.trim()) {
        this.$store.dispatch('showAlert', {
          message: 'Group name is required.',
          type: 'error'
        });
        return;
      }

      this.savingGroupDetails = true;
      try {
        const result = await updateGroupDetails(this.activeConversation.id, this.userId, {
          title: this.detailsForm.title,
          description: this.detailsForm.description
        });

        if (!result.success) {
          throw new Error(result.message);
        }

        this.activeConversation = {
          ...this.activeConversation,
          title: this.detailsForm.title.trim().slice(0, 50),
          description: this.detailsForm.description.trim().slice(0, 180),
          updatedBy: this.userId
        };
        this.setSelectedConversation(this.activeConversation);
        this.isEditingGroup = false;
        this.$store.dispatch('showAlert', {
          message: 'Group details updated.',
          type: 'success'
        });
      } catch (error) {
        console.error('Error saving group details:', error);
        this.$store.dispatch('showAlert', {
          message: error.message || 'Failed to update group details.',
          type: 'error'
        });
      } finally {
        this.savingGroupDetails = false;
      }
    },
    async saveDepartmentDetails() {
      const departmentProfile = this.activeDepartmentProfileForDetails;
      if (!departmentProfile?.id || !this.detailsForm.title.trim()) {
        this.$store.dispatch('showAlert', {
          message: 'Department name is required.',
          type: 'error'
        });
        return;
      }

      this.savingDepartmentDetails = true;
      try {
        const updates = {
          name: this.detailsForm.title,
          description: this.detailsForm.description
        };
        const result = await updateDepartmentProfile(departmentProfile.id, this.userId, updates);

        if (!result.success) {
          throw new Error(result.message);
        }

        this.applyDepartmentProfileUpdate(departmentProfile.id, {
          name: updates.name.trim().slice(0, 60),
          description: (updates.description || '').trim().slice(0, 180)
        });
        this.isEditingDepartment = false;
        this.$store.dispatch('showAlert', {
          message: 'Department details updated.',
          type: 'success'
        });
      } catch (error) {
        console.error('Error saving department details:', error);
        this.$store.dispatch('showAlert', {
          message: error.message || 'Failed to update department details.',
          type: 'error'
        });
      } finally {
        this.savingDepartmentDetails = false;
      }
    },
    handleGroupImageFile(event) {
      const file = event.target.files?.[0];
      if (!file) return;

      if (!file.type.match('image.*')) {
        this.$store.dispatch('showAlert', {
          message: 'Please select an image file.',
          type: 'error'
        });
        return;
      }

      if (file.size > 5 * 1024 * 1024) {
        this.$store.dispatch('showAlert', {
          message: 'Image size should be less than 5MB.',
          type: 'error'
        });
        return;
      }

      this.cancelGroupImageCrop();
      this.groupImagePreview = URL.createObjectURL(file);
      this.$nextTick(() => this.initGroupImageCropper());
    },
    async initGroupImageCropper() {
      const imageElement = this.$refs.groupCropImage;
      if (!imageElement) return;

      const [{ default: Cropper }] = await Promise.all([
        import('cropperjs'),
        import('cropperjs/dist/cropper.css')
      ]);

      this.groupImageCropper = new Cropper(imageElement, {
        aspectRatio: 1,
        viewMode: 1,
        autoCropArea: 1,
        scalable: false,
        rotatable: false,
        zoomable: true,
        responsive: true,
        background: false
      });
    },
    cancelGroupImageCrop() {
      if (this.groupImageCropper) {
        this.groupImageCropper.destroy();
        this.groupImageCropper = null;
      }

      if (this.groupImagePreview) {
        URL.revokeObjectURL(this.groupImagePreview);
      }

      this.groupImagePreview = '';
      if (this.$refs.groupPictureInput) {
        this.$refs.groupPictureInput.value = '';
      }
    },
    async confirmGroupImageCrop() {
      if (!this.groupImageCropper || !this.activeConversation?.id) return;

      this.uploadingGroupImage = true;
      try {
        const canvas = this.groupImageCropper.getCroppedCanvas({
          width: 512,
          height: 512,
          minWidth: 256,
          minHeight: 256,
          maxWidth: 1024,
          maxHeight: 1024,
          fillColor: '#fff',
          imageSmoothingEnabled: true,
          imageSmoothingQuality: 'high'
        });

        if (!canvas) {
          throw new Error('Could not crop the selected image');
        }

        const blob = await new Promise((resolve, reject) => {
          canvas.toBlob((result) => {
            if (result) resolve(result);
            else reject(new Error('Could not prepare the image'));
          }, 'image/jpeg', 0.9);
        });
        const file = new File([blob], 'group-profile.jpg', { type: 'image/jpeg' });
        const result = await uploadGroupProfilePicture(this.activeConversation.id, this.userId, file);

        if (!result.success) {
          throw new Error(result.message);
        }

        this.activeConversation = {
          ...this.activeConversation,
          groupProfilePicture: result.url,
          groupProfilePicturePath: result.path,
          updatedBy: this.userId
        };
        this.setSelectedConversation(this.activeConversation);
        this.failedAvatarKeys = {
          ...this.failedAvatarKeys,
          [this.activeAvatarKey]: false
        };
        this.cancelGroupImageCrop();
        this.$store.dispatch('showAlert', {
          message: 'Group picture updated.',
          type: 'success'
        });
      } catch (error) {
        console.error('Error updating group image:', error);
        this.$store.dispatch('showAlert', {
          message: error.message || 'Failed to update group picture.',
          type: 'error'
        });
      } finally {
        this.uploadingGroupImage = false;
      }
    },
    handleDepartmentImageFile(departmentProfile, event) {
      const file = event.target.files?.[0];
      event.target.value = '';
      if (!file || !departmentProfile?.id) return;

      if (!file.type.match('image.*')) {
        this.$store.dispatch('showAlert', {
          message: 'Please select an image file.',
          type: 'error'
        });
        return;
      }

      if (file.size > 5 * 1024 * 1024) {
        this.$store.dispatch('showAlert', {
          message: 'Image size should be less than 5MB.',
          type: 'error'
        });
        return;
      }

      this.cancelDepartmentImageCrop();
      this.departmentImageTarget = departmentProfile;
      this.departmentImagePreview = URL.createObjectURL(file);
      this.$nextTick(() => this.initDepartmentImageCropper());
    },
    async initDepartmentImageCropper() {
      const imageElement = this.$refs.departmentCropImage;
      if (!imageElement) return;

      const [{ default: Cropper }] = await Promise.all([
        import('cropperjs'),
        import('cropperjs/dist/cropper.css')
      ]);

      this.departmentImageCropper = new Cropper(imageElement, {
        aspectRatio: 1,
        viewMode: 1,
        autoCropArea: 1,
        scalable: false,
        rotatable: false,
        zoomable: true,
        responsive: true,
        background: false
      });
    },
    cancelDepartmentImageCrop() {
      if (this.departmentImageCropper) {
        this.departmentImageCropper.destroy();
        this.departmentImageCropper = null;
      }

      if (this.departmentImagePreview) {
        URL.revokeObjectURL(this.departmentImagePreview);
      }

      this.departmentImagePreview = '';
      this.departmentImageTarget = null;
    },
    async confirmDepartmentImageCrop() {
      if (!this.departmentImageCropper || !this.departmentImageTarget?.id) return;

      this.uploadingDepartmentImage = true;
      try {
        const canvas = this.departmentImageCropper.getCroppedCanvas({
          width: 512,
          height: 512,
          minWidth: 256,
          minHeight: 256,
          maxWidth: 1024,
          maxHeight: 1024,
          fillColor: '#fff',
          imageSmoothingEnabled: true,
          imageSmoothingQuality: 'high'
        });

        if (!canvas) {
          throw new Error('Could not crop the selected image');
        }

        const blob = await new Promise((resolve, reject) => {
          canvas.toBlob((result) => {
            if (result) resolve(result);
            else reject(new Error('Could not prepare the image'));
          }, 'image/jpeg', 0.9);
        });
        const file = new File([blob], 'department-profile.jpg', { type: 'image/jpeg' });
        const departmentId = this.departmentImageTarget.id;
        const result = await uploadDepartmentProfilePicture(departmentId, this.userId, file);

        if (!result.success) {
          throw new Error(result.message);
        }

        this.applyDepartmentPictureUpdate(departmentId, result.url, result.path);
        this.failedAvatarKeys = {
          ...this.failedAvatarKeys,
          [this.departmentProfileAvatarKey(departmentId)]: false,
          [this.activeAvatarKey]: false
        };
        this.cancelDepartmentImageCrop();
        this.$store.dispatch('showAlert', {
          message: 'Department picture updated.',
          type: 'success'
        });
      } catch (error) {
        console.error('Error updating department image:', error);
        this.$store.dispatch('showAlert', {
          message: error.message || 'Failed to update department picture.',
          type: 'error'
        });
      } finally {
        this.uploadingDepartmentImage = false;
      }
    },
    applyDepartmentPictureUpdate(departmentId, profilePicture, profilePicturePath = null) {
      this.applyDepartmentProfileUpdate(departmentId, {
        profilePicture,
        profilePicturePath
      });
    },
    applyDepartmentProfileUpdate(departmentId, updates = {}) {
      this.departmentProfiles = this.departmentProfiles.map((department) => {
        if (department.id !== departmentId) return department;
        return {
          ...department,
          ...updates
        };
      });

      this.conversations = this.conversations.map((conversation) => {
        if (
          getConversationType(conversation) !== 'department' ||
          conversation.departmentProfileId !== departmentId
        ) {
          return conversation;
        }

        return this.withDepartmentProfile(conversation, updates);
      });

      if (
        this.activeConversation &&
        getConversationType(this.activeConversation) === 'department' &&
        this.activeConversation.departmentProfileId === departmentId
      ) {
        this.activeConversation = this.withDepartmentProfile(this.activeConversation, updates);
        this.setSelectedConversation(this.activeConversation);
      }
    },
    withDepartmentProfile(conversation, updates = {}) {
      return {
        ...conversation,
        departmentProfileSnapshot: {
          ...(conversation.departmentProfileSnapshot || {}),
          ...updates
        },
        participantDetails: (conversation.participantDetails || []).map((participant) => {
          if (participant.id !== conversation.departmentProfileId) return participant;
          return {
            ...participant,
            ...(Object.prototype.hasOwnProperty.call(updates, 'name') ? { name: updates.name } : {}),
            ...(Object.prototype.hasOwnProperty.call(updates, 'profilePicture') ? { profilePicture: updates.profilePicture || '' } : {})
          };
        })
      };
    },
    requestDeleteConversation(conversation) {
      if (!conversation || !this.canDeleteConversation(conversation)) return;

      const isGroup = getConversationType(conversation) === 'group';
      const name = getConversationTitle(conversation, this.userId);

      this.openConfirmDialog({
        action: 'deleteConversation',
        payload: conversation,
        title: isGroup ? 'Delete Group Chat' : 'Delete Conversation',
        message: isGroup
          ? `Delete "${name}" for everyone? This removes the group and its message history.`
          : `Delete your conversation with ${name} for both people? This removes the message history.`,
        confirmText: isGroup ? 'Delete Group' : 'Delete Chat',
        icon: 'delete'
      });
    },
    requestRemoveMember(member) {
      this.openConfirmDialog({
        action: 'removeMember',
        payload: member,
        title: 'Remove Member',
        message: `Remove ${member.name || 'this member'} from "${this.activeConversationTitle}"? They will lose access to this group chat.`,
        confirmText: 'Remove',
        icon: 'person_remove'
      });
    },
    requestDeleteGroupImage() {
      this.openConfirmDialog({
        action: 'deleteGroupImage',
        payload: null,
        title: 'Remove Group Picture',
        message: 'Remove this group picture? The group will use the default group icon.',
        confirmText: 'Remove Picture',
        icon: 'hide_image'
      });
    },
    requestDeleteDepartmentProfile(departmentProfile) {
      if (!this.isAdminUser || !departmentProfile?.id) return;

      this.openConfirmDialog({
        action: 'deleteDepartmentProfile',
        payload: departmentProfile,
        title: 'Delete Department Profile',
        message: `Delete "${departmentProfile.name || 'this department'}"? This removes the verified profile and its department chat history for everyone.`,
        confirmText: 'Delete Profile',
        icon: 'delete'
      });
    },
    requestDeleteDepartmentImage(departmentProfile) {
      if (!this.isAdminUser || !departmentProfile?.id) return;

      this.openConfirmDialog({
        action: 'deleteDepartmentImage',
        payload: departmentProfile,
        title: 'Remove Department Picture',
        message: `Remove the profile picture for "${departmentProfile.name || 'this department'}"?`,
        confirmText: 'Remove Picture',
        icon: 'hide_image'
      });
    },
    openConfirmDialog(config) {
      this.confirmDialog = {
        visible: true,
        type: 'danger',
        ...config
      };
    },
    async handleConfirmAction() {
      const { action, payload } = this.confirmDialog;

      if (action === 'deleteConversation') {
        await this.performDeleteConversation(payload);
      } else if (action === 'removeMember') {
        await this.performRemoveMember(payload);
      } else if (action === 'deleteGroupImage') {
        await this.performDeleteGroupImage();
      } else if (action === 'deleteDepartmentProfile') {
        await this.performDeleteDepartmentProfile(payload);
      } else if (action === 'deleteDepartmentImage') {
        await this.performDeleteDepartmentImage(payload);
      }

      this.confirmDialog = {
        visible: false,
        title: '',
        message: '',
        type: 'danger',
        icon: 'warning',
        confirmText: 'Confirm',
        action: null,
        payload: null
      };
    },
    async performDeleteConversation(conversation) {
      try {
        const result = await deleteConversationFromFirestore(conversation.id, this.userId);
        if (!result.success) {
          throw new Error(result.message);
        }

        this.conversations = this.conversations.filter(item => item.id !== conversation.id);

        if (this.activeConversation?.id === conversation.id) {
          if (this.messagesUnsubscribe) {
            this.messagesUnsubscribe();
            this.messagesUnsubscribe = null;
          }
          this.activeConversation = null;
          this.messages = [];
          this.showDetailsPanel = false;
          this.clearSelectedConversation();
          if (this.$route.params.userId || this.$route.query.conversation) {
            this.$router.replace('/chat');
          }
        }

        this.$store.dispatch('showAlert', {
          message: 'Conversation deleted.',
          type: 'success'
        });
      } catch (error) {
        console.error('Error deleting conversation:', error);
        this.$store.dispatch('showAlert', {
          message: error.message || 'Failed to delete conversation.',
          type: 'error'
        });
      }
    },
    async performRemoveMember(member) {
      if (!member?.id || !this.activeConversation?.id) return;

      try {
        const result = await removeGroupMember(this.activeConversation.id, this.userId, member.id);
        if (!result.success) {
          throw new Error(result.message);
        }

        this.activeConversation = {
          ...this.activeConversation,
          participants: this.activeConversation.participants.filter(id => id !== member.id),
          participantDetails: (this.activeConversation.participantDetails || []).filter(
            participant => participant.id !== member.id
          ),
          updatedBy: this.userId
        };
        this.setSelectedConversation(this.activeConversation);

        this.$store.dispatch('showAlert', {
          message: 'Member removed.',
          type: 'success'
        });
      } catch (error) {
        console.error('Error removing member:', error);
        this.$store.dispatch('showAlert', {
          message: error.message || 'Failed to remove member.',
          type: 'error'
        });
      }
    },
    async performDeleteGroupImage() {
      if (!this.activeConversation?.id) return;

      try {
        const result = await deleteGroupProfilePicture(this.activeConversation.id, this.userId);
        if (!result.success) {
          throw new Error(result.message);
        }

        this.activeConversation = {
          ...this.activeConversation,
          groupProfilePicture: null,
          groupProfilePicturePath: null,
          updatedBy: this.userId
        };
        this.setSelectedConversation(this.activeConversation);
        this.$store.dispatch('showAlert', {
          message: 'Group picture removed.',
          type: 'success'
        });
      } catch (error) {
        console.error('Error deleting group image:', error);
        this.$store.dispatch('showAlert', {
          message: error.message || 'Failed to remove group picture.',
          type: 'error'
        });
      }
    },
    async performDeleteDepartmentProfile(departmentProfile) {
      if (!this.isAdminUser || !departmentProfile?.id) return;

      try {
        const result = await deleteDepartmentProfile(departmentProfile.id, this.userId);
        if (!result.success) {
          throw new Error(result.message);
        }

        this.departmentProfiles = this.departmentProfiles.filter(
          profile => profile.id !== departmentProfile.id
        );
        this.conversations = this.conversations.filter((conversation) => {
          return !(getConversationType(conversation) === 'department' &&
            conversation.departmentProfileId === departmentProfile.id);
        });

        if (
          this.activeConversation &&
          getConversationType(this.activeConversation) === 'department' &&
          this.activeConversation.departmentProfileId === departmentProfile.id
        ) {
          if (this.messagesUnsubscribe) {
            this.messagesUnsubscribe();
            this.messagesUnsubscribe = null;
          }
          this.activeConversation = null;
          this.messages = [];
          this.clearSelectedConversation();
        }

        this.$store.dispatch('showAlert', {
          message: 'Department profile deleted.',
          type: 'success'
        });
      } catch (error) {
        console.error('Error deleting department profile:', error);
        this.$store.dispatch('showAlert', {
          message: error.message || 'Failed to delete department profile.',
          type: 'error'
        });
      }
    },
    async performDeleteDepartmentImage(departmentProfile) {
      if (!this.isAdminUser || !departmentProfile?.id) return;

      try {
        const result = await deleteDepartmentProfilePicture(departmentProfile.id, this.userId);
        if (!result.success) {
          throw new Error(result.message);
        }

        this.applyDepartmentPictureUpdate(departmentProfile.id, '', null);
        this.$store.dispatch('showAlert', {
          message: 'Department picture removed.',
          type: 'success'
        });
      } catch (error) {
        console.error('Error deleting department image:', error);
        this.$store.dispatch('showAlert', {
          message: error.message || 'Failed to remove department picture.',
          type: 'error'
        });
      }
    },
    handleBack() {
      if (this.activeConversation && this.isMobileView) {
        this.activeConversation = null;
        this.messages = [];
        this.clearSelectedConversation();
        if (this.messagesUnsubscribe) {
          this.messagesUnsubscribe();
          this.messagesUnsubscribe = null;
        }
        if (this.$route.params.userId || this.$route.query.conversation) {
          this.$router.replace('/chat');
        }
        return;
      }

      this.$router.back();
    },
    handleResize() {
      this.isMobileView = window.innerWidth < 768;
    },
    initials(name) {
      return (name || '?')
        .split(' ')
        .filter(Boolean)
        .map(part => part[0])
        .join('')
        .substring(0, 2)
        .toUpperCase();
    },
    connectionAvatarKey(userId) {
      return `connection-${userId}`;
    },
    memberAvatarKey(userId) {
      return `member-${userId}`;
    },
    adminAvatarKey(userId) {
      return `department-admin-${userId}`;
    },
    departmentProfileAvatarKey(departmentId) {
      return `department-profile-${departmentId}`;
    },
    canShowAvatar(key, url) {
      return Boolean(url) && !this.failedAvatarKeys[key];
    },
    markAvatarFailed(key) {
      this.failedAvatarKeys = {
        ...this.failedAvatarKeys,
        [key]: true
      };
    }
  },
  async mounted() {
    window.addEventListener('resize', this.handleResize);

    if (this.connections.length === 0) {
      await this.$store.dispatch('fetchUserConnections');
    }

    await this.loadConversations();

    const routeUserId = this.$route.params.userId;
    if (routeUserId) {
      await this.loadConversationWithUser(routeUserId);
    }
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.handleResize);
    this.cancelGroupImageCrop();
    this.cancelDepartmentImageCrop();
    if (this.conversationsUnsubscribe) this.conversationsUnsubscribe();
    if (this.messagesUnsubscribe) this.messagesUnsubscribe();
  },
  watch: {
    '$route.params.userId': async function(userId) {
      if (userId) {
        await this.loadConversationWithUser(userId);
      }
    },
    '$route.query.conversation': function(conversationId) {
      if (!conversationId || this.activeConversation?.id === conversationId) return;
      const conversation = this.conversations.find(item => item.id === conversationId);
      if (conversation) {
        this.selectConversation(conversation, { keepRoute: true });
      }
    },
    userId(newUserId) {
      if (newUserId) {
        this.loadConversations();
      }
    }
  }
};
</script>

<style scoped>
@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.animate-spin {
  animation: spin 1s linear infinite;
}
</style>
